import { useCallback, useEffect, useMemo, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useCampaignTracking } from '#src/app/shared/hooks/campaignTracking';

import { deleteMediaHit } from '../../../api/deleteMediaHit';
import type { MediaHitComment } from '../../../shared/context/types';

type UseDeleteMediaLogicProps = {
	media: MediaHitComment;
	petitionId: string;
	onResult: (result: { value: 'success' | 'error' | undefined }) => void;
	onOpenChange: (open: boolean) => void;
};

export function useDeleteMediaLogic({ media, petitionId, onResult, onOpenChange }: UseDeleteMediaLogicProps) {
	const [isDeleteLoading, setIsDeleteLoading] = useState(false);
	const [deleteStatus, setDeleteStatus] = useState<'success' | 'error' | undefined>(undefined);
	const utilityContext = useUtilityContext();
	const { track, trackError } = useCampaignTracking();
	const trackingData = useMemo(
		() => ({ petition_id: petitionId, media_id: media.id, media_url: media.url }),
		[petitionId, media],
	);

	useEffect(() => {
		if (deleteStatus === 'success') {
			setTimeout(() => {
				onResult({ value: deleteStatus });
				onOpenChange(false);
			}, 1000);
		}
	}, [deleteStatus, onOpenChange, onResult]);

	const handleCancel = useCallback(() => {
		track('campaign_media_hit_delete_modal_cancel_button_click', trackingData);
		onResult({ value: undefined });
		onOpenChange(false);
	}, [onOpenChange, track, trackingData, onResult]);

	const handleDelete = useCallback(async () => {
		track('campaign_media_hit_delete_modal_delete_button_click', trackingData);
		setIsDeleteLoading(true);
		try {
			const deleteResult = await deleteMediaHit(media.id, utilityContext);
			if (deleteResult?.deleteComment?.success) {
				setDeleteStatus('success');
				track('campaign_media_hit_delete_success', trackingData);
			} else {
				setDeleteStatus('error');
				track('campaign_media_hit_delete_failure', {
					...trackingData,
					...(deleteResult?.deleteComment?.reason ? { errors: deleteResult.deleteComment.reason } : {}),
				});
			}
		} catch (err) {
			setDeleteStatus('error');
			trackError('campaign_media_hit_delete_error', err, { context: trackingData });
		} finally {
			setIsDeleteLoading(false);
		}
	}, [track, trackingData, media, utilityContext, trackError]);

	return { isDeleteLoading, deleteStatus, handleCancel, handleDelete };
}
